/**
 * One notification row on web, shared by the full Notifications page and the
 * My Tabbio Notifications pop-out (which passes no `onDelete`, so it shows no
 * "…" menu). Styled by the `.notification-*` rules in global.css; the pop-out
 * compacts them under `.tabbio-popover` (web-popover.css).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { NotificationSummary, SystemSenderIdentity } from "@tabbio/types";
import { useLocale } from "../../hooks/useLocale";
import { formatRelativeTime } from "../../lib/cv-list-format";
import { WebMenu, WebMenuItem } from "../web/WebMenu";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { getSystemSenderIdentityFromNotificationData } from "../../lib/system-sender-identity";
import {
  getCompanyMonogram,
  readNotificationCompany,
  type NotificationCompanyIdentity,
} from "../../lib/notification-company";

const systemSenderAvatars: Record<"tabbio" | "founder", string> = {
  tabbio: require("../../../assets/branding/admin-sender-tabbio.png"),
  founder: require("../../../assets/branding/admin-sender-founder.png"),
};

const TYPE_ICON: Record<string, string> = {
  application_update: "task_alt",
  applicationUpdate: "task_alt",
  job_match: "work",
  jobMatch: "work",
  auto_apply: "bolt",
  autoApply: "bolt",
  profile_view: "visibility",
  profileView: "visibility",
  interview: "event_available",
  nova_approval: "smart_toy",
  nova_result: "smart_toy",
  nova_failed: "smart_toy",
  nova_paused: "smart_toy",
  report_update: "flag",
  system: "notifications",
  system_message: "notifications",
  message: "chat",
};

function NotificationRowAvatar({
  company,
  iconName,
  senderIdentity,
  senderAvatar,
}: {
  company: NotificationCompanyIdentity;
  iconName: string;
  senderIdentity: SystemSenderIdentity | null;
  senderAvatar: string | null;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [company.logo]);
  if (senderIdentity && senderAvatar) {
    return (
      <img
        className="notification-avatar notification-avatar-person"
        src={senderAvatar}
        alt=""
      />
    );
  }
  return (
    <span className="notification-avatar" aria-hidden>
      {company.logo && !logoFailed ? (
        <img src={company.logo} alt="" onError={() => setLogoFailed(true)} />
      ) : company.name ? (
        getCompanyMonogram(company.name)
      ) : (
        <MaterialSymbol name={iconName} size={22} />
      )}
    </span>
  );
}

export function NotificationWebRow({
  item,
  onPress,
  onDelete,
  onPrefetch,
}: {
  item: NotificationSummary;
  onPress: (item: NotificationSummary) => void;
  /** Without it there is no "…" menu (the My Tabbio pop-out). */
  onDelete?: (item: NotificationSummary) => void;
  onPrefetch: (item: NotificationSummary) => void;
}) {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const senderIdentity = getSystemSenderIdentityFromNotificationData(item.data);
  const company = readNotificationCompany(item.data);
  const subtitle = company.name
    ? [company.name, company.location].filter(Boolean).join(" · ")
    : item.body;
  const relativeTime = formatRelativeTime(item.createdAt);
  const when = t(`notifications.time.${relativeTime.key}`, {
    value: relativeTime.count.toLocaleString(locale),
  });
  const source =
    typeof item.data?.source === "string" ? item.data.source : null;
  const closeMenu = () => {
    menuAnchor?.focus();
    setMenuAnchor(null);
  };

  return (
    <div
      className={`notification-row${item.read ? "" : " is-unread"}`}
      onMouseEnter={() => onPrefetch(item)}
    >
      {!item.read ? (
        <span className="notification-unread-dot" aria-hidden />
      ) : null}
      <button
        type="button"
        className="notification-row-main"
        onFocus={() => onPrefetch(item)}
        onPointerDown={() => onPrefetch(item)}
        onClick={() => onPress(item)}
        aria-label={[
          !item.read && t("notifications.unread"),
          item.title,
          subtitle,
          when,
        ]
          .filter(Boolean)
          .join(". ")}
      >
        <NotificationRowAvatar
          company={company}
          iconName={TYPE_ICON[item.type] ?? "notifications"}
          senderIdentity={senderIdentity}
          senderAvatar={
            senderIdentity ? systemSenderAvatars[senderIdentity.key] : null
          }
        />
        <span className="notification-copy">
          {company.name ? (
            <>
              <span className="notification-title" dir="auto">
                {item.title}
              </span>
              <span
                className="notification-body notification-company"
                dir="auto"
              >
                {subtitle}
              </span>
            </>
          ) : (
            <span className="notification-story" dir="auto">
              <span className="notification-title">{item.title}</span>
              {subtitle ? (
                <>
                  {" "}
                  <span className="notification-body">{subtitle}</span>
                </>
              ) : null}
            </span>
          )}
          {company.matchPercent != null || source ? (
            <span className="notification-meta" dir="auto">
              {[
                company.matchPercent != null
                  ? t("notifications.matchChip", {
                      score: company.matchPercent,
                    })
                  : null,
                source,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          ) : null}
        </span>
      </button>
      <div className="notification-aside">
        <time dateTime={item.createdAt} className="notification-time">
          {when}
        </time>
        {onDelete ? (
          <button
            type="button"
            className="notification-more"
            aria-label={`${t("notifications.moreActions")}: ${item.title}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            onClick={(event) =>
              setMenuAnchor(menuAnchor ? null : event.currentTarget)
            }
          >
            <MaterialSymbol name="more_horiz" size={20} />
          </button>
        ) : null}
      </div>
      {onDelete ? (
        <WebMenu
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchor={menuAnchor}
          minWidth={180}
        >
          <WebMenuItem
            label={t("actions.delete")}
            onSelect={() => onDelete(item)}
          />
        </WebMenu>
      ) : null}
    </div>
  );
}
